import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import CandleChart from '../components/CandleChart';
import { useWebSocket } from '../hooks/useWebSocket';
import { aiApi, AI_WS_URL } from '../api';

const SYMBOLS   = ['BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT', 'ADAUSDT'];
const INTERVALS = ['1m', '5m', '15m', '30m', '1h', '4h', '1d'];
const TF_REFETCH = { '1m': 60_000, '5m': 300_000, '15m': 900_000, '30m': 1_800_000,
                     '1h': 3_600_000, '4h': 14_400_000, '1d': 86_400_000 };

const SIG_COLOR  = { buy: '#34d399', sell: '#f87171', hold: '#64748b' };
const SIG_LABEL  = { buy: '▲ COMPRA', sell: '▼ VENDA', hold: '— NEUTRO' };
const SIG_ARROW  = { buy: '▲', sell: '▼', hold: '—' };

export default function Market() {
  const [symbol,      setSymbol]      = useState('BTCUSDT');
  const [interval,    setInterval]    = useState('1h');
  const [showSignals, setShowSignals] = useState(true);
  const [minConf,     setMinConf]     = useState(75);
  const [showArena,   setShowArena]   = useState(false);
  const [liveCandle,  setLiveCandle]  = useState(null);

  const { data: tick, connected } = useWebSocket(`${AI_WS_URL}/ws/prices/${symbol}`);

  // Candles — refresh at candle-close cadence.
  // limit=1500 (~2 meses de 1h) em vez do historico completo: 565k candles
  // de 1m sao ~20MB de JSON e o chart nao mostra tudo de qualquer forma.
  const { data: candles = [], isFetching } = useQuery({
    queryKey: ['candles', symbol, interval],
    queryFn: () => aiApi.get(`/candles/${symbol}?interval=${interval}&limit=1500`).then(r => r.data),
    staleTime: 0,
    refetchInterval: TF_REFETCH[interval] || 60_000,
  });

  // Historical signal TRANSITIONS (sparse — only where signal changes)
  const { data: signalMarkers = [] } = useQuery({
    queryKey: ['signal-markers', symbol, interval, minConf],
    queryFn: () =>
      aiApi.get(`/signals/history/${symbol}?interval=${interval}&limit=300&min_confidence=${minConf / 100}`)
           .then(r => r.data),
    enabled: showSignals,
    staleTime: 0,
    refetchInterval: TF_REFETCH[interval] || 60_000,
  });

  // LIVE current signal — updates frequently
  const { data: liveSignal } = useQuery({
    queryKey: ['signal-live', symbol, interval],
    queryFn: () => aiApi.get(`/signals/live/${symbol}?interval=${interval}`).then(r => r.data),
    staleTime: 0,
    refetchInterval: TF_REFETCH[interval] || 60_000,
  });

  // Arena trades overlay
  const { data: arenaEvents = [] } = useQuery({
    queryKey: ['arena-trades'],
    queryFn: () => aiApi.get('/arena/trades').then(r => r.data),
    enabled: showArena,
    refetchInterval: showArena ? 10_000 : false,
  });

  // 24h ticker
  const { data: ticker } = useQuery({
    queryKey: ['ticker', symbol],
    queryFn: () => aiApi.get(`/ticker/${symbol}`).then(r => r.data),
    refetchInterval: 15_000,
  });

  useEffect(() => {
    if (!tick || !tick.closed) return;
    setLiveCandle({ time: tick.timestamp, open: tick.open, high: tick.high, low: tick.low, close: tick.price });
  }, [tick]);

  // Merge markers
  const allMarkers = [
    ...(showSignals ? signalMarkers : []),
    ...(showArena
      ? arenaEvents
          .filter(e => e.symbol?.toUpperCase() === symbol && e.interval === interval)
          .map(e => ({
            time: e.time,
            signal: e.side === 'buy' ? 'buy' : 'sell',
            confidence: 1,
            _label: `${e.bot_emoji}${e.close ? '✓' : ''}`,
          }))
      : []),
  ];

  const price   = tick?.price ?? parseFloat(ticker?.lastPrice ?? 0);
  const change  = parseFloat(ticker?.priceChangePercent ?? 0);
  const chColor = change >= 0 ? '#34d399' : '#f87171';

  const sig  = liveSignal?.signal  || 'hold';
  const conf = liveSignal?.confidence || 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* ── Top bar: symbol + live price ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        {/* Symbol pills */}
        <div style={{ display: 'flex', gap: 6 }}>
          {SYMBOLS.map(s => (
            <button key={s} onClick={() => setSymbol(s)} style={{
              background: symbol === s ? '#1e3a5f' : '#1e293b',
              color: symbol === s ? '#38bdf8' : '#64748b',
              border: `1px solid ${symbol === s ? '#38bdf8' : '#334155'}`,
              borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
              fontWeight: 700, fontSize: 13,
            }}>{s.replace('USDT','')}</button>
          ))}
        </div>

        {/* Live price */}
        {price > 0 && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ fontSize: 26, fontWeight: 900, color: '#e2e8f0', letterSpacing: '-0.5px' }}>
              ${price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </span>
            <span style={{ fontSize: 14, fontWeight: 700, color: chColor }}>
              {change >= 0 ? '+' : ''}{change.toFixed(2)}%
            </span>
          </div>
        )}

        {/* WS status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: connected ? '#34d399' : '#64748b',
                        boxShadow: connected ? '0 0 6px #34d399' : 'none' }} />
          <span style={{ fontSize: 11, color: '#64748b' }}>{connected ? 'Live' : 'Offline'}</span>
        </div>
      </div>

      {/* ── Stats strip ── */}
      {ticker && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            { label: 'Máx 24h', value: `$${parseFloat(ticker.highPrice).toLocaleString()}` },
            { label: 'Mín 24h', value: `$${parseFloat(ticker.lowPrice).toLocaleString()}` },
            { label: 'Volume',  value: parseFloat(ticker.volume).toLocaleString(undefined, { maximumFractionDigits: 0 }) },
            { label: 'Trades',  value: parseInt(ticker.count ?? 0).toLocaleString() },
          ].map(({ label, value }) => (
            <div key={label} style={{ background: '#1e293b', borderRadius: 8, padding: '8px 14px', flex: 1, minWidth: 110 }}>
              <div style={{ fontSize: 10, color: '#475569', marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8' }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Chart + Live Signal (side by side on wide screens) ── */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* Chart card */}
        <div style={{ flex: 1, minWidth: 0, background: '#1e293b', borderRadius: 12, padding: '14px 16px' }}>
          {/* TF selector + controls */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            {INTERVALS.map(iv => (
              <button key={iv} onClick={() => setInterval(iv)} style={{
                background: interval === iv ? '#334155' : 'transparent',
                color:      interval === iv ? '#e2e8f0' : '#475569',
                border: 'none', borderRadius: 5, padding: '3px 10px',
                cursor: 'pointer', fontSize: 12, fontWeight: interval === iv ? 700 : 400,
              }}>{iv}</button>
            ))}

            <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center', fontSize: 12 }}>
              {/* Signals toggle */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#64748b', cursor: 'pointer' }}>
                <input type="checkbox" checked={showSignals} onChange={e => setShowSignals(e.target.checked)}
                  style={{ accentColor: '#38bdf8' }} />
                Sinais
              </label>
              {showSignals && (
                <>
                  <input type="range" min={60} max={90} value={minConf}
                    onChange={e => setMinConf(Number(e.target.value))}
                    style={{ width: 70, accentColor: '#38bdf8' }} />
                  <span style={{ color: '#38bdf8', fontWeight: 700, minWidth: 30 }}>{minConf}%</span>
                </>
              )}
              {/* Arena overlay */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#64748b', cursor: 'pointer' }}>
                <input type="checkbox" checked={showArena} onChange={e => setShowArena(e.target.checked)}
                  style={{ accentColor: '#fbbf24' }} />
                Arena
              </label>
            </div>
          </div>

          {candles.length > 0 && (
            <div style={{ fontSize: 10, color: '#334155', marginBottom: 6 }}>
              {candles.length} candles ·{' '}
              {candles[0] ? new Date(candles[0].time * 1000).toLocaleDateString('pt-BR') : ''}
              {' → hoje'}{' · '}
              {signalMarkers.length > 0 && showSignals && `${signalMarkers.length} transições de sinal`}
            </div>
          )}

          {isFetching && !candles.length
            ? <div style={{ height: 380, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569' }}>
                Carregando...
              </div>
            : <CandleChart data={candles} liveCandle={liveCandle} markers={allMarkers} height={400} />
          }
        </div>

        {/* Live signal panel */}
        <div style={{ width: 180, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Current signal */}
          <div style={{
            background: '#1e293b', borderRadius: 12, padding: '16px 14px', textAlign: 'center',
            border: `1px solid ${SIG_COLOR[sig]}44`,
          }}>
            <div style={{ fontSize: 10, color: '#475569', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
              {symbol.replace('USDT','')} · {interval}
            </div>
            <div style={{ fontSize: 28, fontWeight: 900, color: SIG_COLOR[sig] }}>
              {SIG_ARROW[sig]}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: SIG_COLOR[sig], marginTop: 4 }}>
              {SIG_LABEL[sig]}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#e2e8f0', marginTop: 8 }}>
              {(conf * 100).toFixed(0)}%
            </div>
            <div style={{ fontSize: 10, color: '#475569', marginTop: 4 }}>confiança</div>
            {liveSignal?.computed_at && (
              <div style={{ fontSize: 9, color: '#334155', marginTop: 6 }}>
                atualizado {new Date(liveSignal.computed_at + 'Z').toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
            {liveSignal?.trained === false && (
              <div style={{ fontSize: 10, color: '#f59e0b', marginTop: 4 }}>
                modelo não treinado
              </div>
            )}
          </div>

          {/* Indicators */}
          {liveSignal?.indicators && (
            <div style={{ background: '#1e293b', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 10, color: '#475569', marginBottom: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                Indicadores
              </div>
              {Object.entries(liveSignal.indicators).filter(([, v]) => v != null).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 5 }}>
                  <span style={{ color: '#475569' }}>{k}</span>
                  <span style={{ color: '#94a3b8', fontWeight: 600, fontFamily: 'monospace' }}>
                    {typeof v === 'number' ? v.toFixed(3) : v}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Probabilities */}
          {liveSignal?.probabilities && (
            <div style={{ background: '#1e293b', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 10, color: '#475569', marginBottom: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                Probabilidades
              </div>
              {[['buy','#34d399'],['hold','#475569'],['sell','#f87171']].map(([s, c]) => {
                const p = liveSignal.probabilities[s] || 0;
                return (
                  <div key={s} style={{ marginBottom: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginBottom: 2 }}>
                      <span style={{ color: c }}>{s}</span>
                      <span style={{ color: c, fontWeight: 700 }}>{(p * 100).toFixed(0)}%</span>
                    </div>
                    <div style={{ height: 3, background: '#0f172a', borderRadius: 2 }}>
                      <div style={{ height: '100%', width: `${p * 100}%`, background: c, borderRadius: 2 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
